import { Logger } from '@gitbook/util-debug';
import { List } from '@gitbook/util-immutable';
import { filterOutNullable } from '@gitbook/util-ts';

import { isType, MODEL_TYPES } from '../constants';
import { generateKey } from '../utils';
import { Block } from './block';
import { BlockRange } from './block-range';
import { Data } from './data';
import type { Fragment } from './fragment';
import type { Inline } from './inline';
import { AbstractNode, memoizeMethods } from './node';
import { Text } from './text';
import type { TextRange } from './text-range';
import type { SelectionRange } from './value';

const logger = Logger('slate:document');

/**
 * Main node in an editor value.
 */
export class Document extends AbstractNode {
    declare public readonly key: string;
    declare public readonly data: Data;
    declare public readonly nodes: List<Block>;
    declare public readonly fragments: never;
    declare public readonly fragment: never;
    declare public readonly isVoid: never;
    declare public readonly type: never;

    get object(): 'document' {
        return 'document';
    }

    /*
     * Check if the document is empty.
     * Returns true if all it's children nodes are empty.
     */
    get isEmpty(): boolean {
        return !this.nodes.some((child) => !child.isEmpty);
    }

    /*
     * Get the concatenated text of all the document's children.
     */
    get text(): string {
        return this.getText();
    }

    /*
     * Check if `input` is a `Document`.
     */
    public static isDocument(input: any): input is Document {
        return isType('DOCUMENT', input);
    }

    /*
     * Create a new `Document` with `attrs`.
     */
    public static create(
        attrs: Partial<
            Omit<Document, 'nodes' | 'data'> & {
                nodes: Block[] | List<Block>;
                data: object | Data;
            }
        > = {}
    ): Document {
        return (Document.createRecord as (props: object) => Document)({
            key: attrs.key || generateKey(),
            data: Data.create(attrs.data),
            nodes: List.isList(attrs.nodes) ? attrs.nodes : List.create(attrs.nodes),
        });
    }

    /*
     * Get a fragment of the node at a `range`.
     * If `limitToCommonParent` is `false`, the returned Document includes all ancestors
     * of the nodes in the given `range`.
     * The default (or when `limitToCommonParent` is `true`) strips all the common ancestors
     * and only return the lowest level nodes in the given `range`.
     */
    public getFragmentAtRange(range: SelectionRange, limitToCommonParent = true): Document {
        range = range.normalize(this);
        if (range.isUnset) {
            return Document.create();
        }

        if (BlockRange.isBlockRange(range)) {
            return Document.create({
                nodes: range.getBlocks(this),
            });
        }

        let node: Document | Fragment = this;

        // Make sure the children exist.
        const { startKey, startOffset, endKey, endOffset } = range;

        const startText = node.assertDescendant(startKey!);
        const endText = node.assertDescendant(endKey!);

        if (range.isCollapsed && Text.isText(startText) && startText.isEmpty) {
            const parent = node.getParent(startText.key);
            if (Block.isBlock(parent) && parent.isVoid) {
                // if we are selecting a void block (and only a void block), copy it
                return Document.create({ nodes: [parent] });
            }
        }

        const startParentFragment = node.getFragmentParent(startKey!);
        const endParentFragment = node.getFragmentParent(endKey!);
        if (
            startParentFragment?.object === 'fragment' &&
            endParentFragment?.object === 'fragment'
        ) {
            if (startParentFragment.key === endParentFragment.key) {
                // if the range is inside a fragment, limit the algo to that fragment
                node = startParentFragment;
            } else {
                // Cannot create a fragment from a selection spanning multiple fragments
                throw new Error(
                    'Cannot create a fragment from a range spanning multiple fragments.'
                );
            }
        }

        // Split at the start and end.
        let child = startText;
        let previous;

        // Split node at the start of the range.
        // We keep track of all the ancestors (after split) that include the start of the range
        // (we split right before the start of `range` so we need to take the newly created key
        // from the split).
        const startParents: string[] = [];
        let parent = node.getParent(child.key);
        while (parent) {
            const index = parent.getChildIndex(child.key);
            const position =
                child.object === 'text' ? startOffset : child.getChildIndex(previous.key);

            parent = parent.splitNode(index, position);
            node = node.updateNode(parent);
            previous = parent.nodes.get(index + 1);
            // Unshift the node we just created as parent
            startParents.unshift(previous.key);
            child = parent;
            parent = node.getParent(child.key);
        }

        child = startKey === endKey ? node.getNextText(startKey!)! : endText;

        // Split node at the end of the range.
        // We keep track of all the ancestors (after split) that include the end of the range
        // (we split right after the end of `range` so we take the key from before the split).
        const endParents: string[] = [];
        parent = node.getParent(child.key);
        while (parent) {
            const index = parent.getChildIndex(child.key);
            const position =
                child.object === 'text'
                    ? startKey === endKey
                        ? endOffset - startOffset
                        : endOffset
                    : child.getChildIndex(previous.key);

            parent = parent.splitNode(index, position);
            node = node.updateNode(parent);
            previous = parent.nodes.get(index + 1);
            // Unshift the node we just split as parent
            endParents.unshift(parent.nodes.get(index)!.key);
            child = parent;
            parent = node.getParent(child.key);
        }

        if (limitToCommonParent) {
            // Look for the lowest common parent in the tree that has
            // only Blocks children
            let lowestCommonParent: Fragment | Block | undefined;
            startParents.forEach((startParentKey, i) => {
                const matchingEndParentKey = endParents[i];
                if (matchingEndParentKey === startParentKey) {
                    const commonParent = node.getByKey(startParentKey);
                    if (
                        commonParent &&
                        commonParent.object !== 'text' &&
                        commonParent.nodes.every((node) => Block.isBlock(node))
                    ) {
                        lowestCommonParent = commonParent as Fragment | Block;
                    }
                }
            });

            if (lowestCommonParent) {
                return Document.create({ nodes: lowestCommonParent.nodes as List<Block> });
            }

            // Fallback to returning the whole tree if we couldn't find a better
            // common ancestor lower in the tree
        }

        // Get the start and end nodes.
        const startNode = node.getNextSibling(node.getFurthestAncestor(startKey!)!.key)!;
        const endNode =
            startKey === endKey
                ? node.getNextSibling(startNode.key)
                : node.getNextSibling(node.getFurthestAncestor(endKey!)!.key);

        // Get children range of nodes from start to end nodes
        const startIndex = node.getChildIndex(startNode.key);
        const endIndex = node.getChildIndex(endNode!.key);
        const nodes = node.nodes.slice(startIndex, endIndex);

        // Return a new document fragment.
        return Document.create({ nodes });
    }

    /**
     * Fix potential duplicated keys in the document.
     */
    public fixDuplicatedKeys(): Document {
        const keys = new Set<string>();
        return this.mapDescendants((node) => {
            if (keys.has(node.key)) {
                logger.warning(
                    `duplicated key "${node.key}" (object "${node.object}" and type "${node.type}")`
                );

                node = node.regenerateKey();
            }
            keys.add(node.key);

            return node;
        });
    }

    /*
     * Get the closest inline nodes for each text node in a `range`.
     */
    public getInlinesAtRange(range: TextRange, withFragments = false): List<Inline> {
        const array = this.getInlinesAtRangeAsArray(range, withFragments);
        return List.create(array).unique();
    }

    /*
     * Get the closest inline nodes for each text node in a `range` as an array.
     */
    public getInlinesAtRangeAsArray(range: TextRange, withFragments = false): Inline[] {
        range = range.normalize(this);
        if (range.isUnset) {
            return [];
        }

        return this.getTextsAtRangeAsArray(range, withFragments)
            .map((text) => this.getClosestInline(text.key))
            .filter(filterOutNullable);
    }

    /*
     * Get all of the text nodes in a `range` as an array.
     */
    public getTextsAtRangeAsArray(range: TextRange, withFragments = false): Text[] {
        range = range.normalize(this);
        if (range.isUnset) {
            return [];
        }
        const { startKey, endKey } = range;
        if (!startKey || !endKey) {
            return [];
        }
        return this.getTextsBetweenPositionsAsArray(startKey, endKey, withFragments);
    }
}

/*
 * Attach a pseudo-symbol for type checking.
 */

Document.prototype[MODEL_TYPES.DOCUMENT] = true;

memoizeMethods(Document);
